/* ============================================================
   DNB Cart Write Queue v2 (2026-08-17)

   THE BUG IT FIXES: Shopify's cart endpoints are read-modify-write
   on the whole cart, with no locking. Two writes that overlap both
   read the same starting cart, each computes its own result, and
   whichever responds last wins outright - the other edit is silently
   discarded. Both return 200.

   Reproduced at raw HTTP level, no UI involved:
     POST /cart/change.js {"line":1,"quantity":0}   (remove 30 cups)
     POST /cart/add.js    {items:[{id:<60 cups>}]}  (add 60 cups)
   fired together -> cart ends up "60 cups + 30 cups". The removal is
   gone, so the item the customer just deleted reappears.

   Three things make it easy to hit on this store: the theme removes
   by LINE INDEX (fragile against a stale snapshot), nothing blocks the
   Add button while a remove is in flight, and cart writes here take
   ~400-600ms - that is the whole overlap window.

   WHAT THIS DOES: chains cart WRITES so two can never be in flight at
   once. Each write waits for the previous one to respond, then goes.

   WHY IT COSTS NOTHING: with no write in flight the queue is already
   resolved, so the request is dispatched one microtask later - the
   same frame, before the browser paints, no network delay. The ONLY
   time anything waits is when a second write would otherwise have
   raced the first, and in that case it was going to lose data anyway.
   Reads are never queued: /cart.js and /cart?section_id=... go
   straight through, so the drawer render is untouched.

   LOAD ORDER: this file is loaded BEFORE dnb-instant-drawer.js on
   purpose. Wrapping window.fetch in that order leaves the drawer
   outermost, so its placeholder still paints synchronously on click -
   only the network call itself is serialised.

   SAFETY: fails open. A rejected write does not break the chain, a
   synchronous throw does not wedge it, and a hung request releases the
   queue after STALL_MS so one dead socket can never block the cart.
   The stall timer starts when a request is DISPATCHED, not when it is
   queued, so a write waiting behind a slow one can never time out
   before it has even been sent. Not installed twice. No UI, no
   storage, no libraries.

   KNOWN LIMIT: the queue is per browser tab. Two tabs editing the same
   cart at the same moment can still race - that is inherent to
   Shopify's cart API and cannot be fixed from the storefront.

   Health readout in the console: __dnbCQ
     writes    - cart writes seen
     deferred  - how many actually had to wait for another write
     maxWaitMs - longest any write waited
     stalls    - times the stall release fired
   ============================================================ */
(function () {
  'use strict';

  if (window.__dnbCQ) return; /* already installed */

  /* A write that never responds must not block the cart forever. This
     only releases the QUEUE - the original request is untouched and
     still resolves or rejects normally for whoever called it. */
  var STALL_MS = 6000;

  /* Below this, a "wait" is just promise scheduling noise rather than
     real queueing, so it is not counted as deferred. */
  var WAIT_EPSILON_MS = 5;

  var S = window.__dnbCQ = { v: 2, state: 'off', writes: 0, deferred: 0, maxWaitMs: 0, stalls: 0, errors: 0 };

  var orig = window.fetch;
  if (typeof orig !== 'function') return;

  /* POST to the endpoints that MUTATE the cart. Everything else -
     including every read - bypasses the queue entirely. */
  function isCartWrite(url, method) {
    if (method !== 'POST') return false;
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url);
  }

  var tail = Promise.resolve(); /* resolves when the last write settled */
  var pending = 0;              /* writes enqueued but not yet settled */

  function noop() {}

  window.fetch = function (input, init) {
    var url = '', method = 'GET';
    try {
      url = String((input && input.url) || input || '');
      method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    } catch (e) { S.errors++; }

    if (!isCartWrite(url, method)) return orig.apply(this, arguments);

    var self = this, args = arguments;
    S.writes++;
    pending++;

    var queuedAt = Date.now();
    var prev = tail;
    var release;
    tail = new Promise(function (res) { release = res; });

    var run = prev.then(function () {
      /* measured at DISPATCH, so it reflects real waiting rather than
         whether some earlier promise had finished flushing */
      var waited = Date.now() - queuedAt;
      if (waited >= WAIT_EPSILON_MS) {
        S.deferred++;
        if (waited > S.maxWaitMs) S.maxWaitMs = waited;
      }

      var done = false;
      function free() {
        if (done) return;
        done = true;
        clearTimeout(timer);
        if (pending > 0) pending--;
        release();
      }
      /* The stall timer starts HERE, when the request actually goes out -
         not when it was queued. Otherwise a write that sat in the queue
         could time out before it had even been dispatched, and the queue
         would let two writes overlap under load. */
      var timer = setTimeout(function () {
        if (done) return;
        done = true;
        S.stalls++;
        if (pending > 0) pending--;
        release();
      }, STALL_MS);

      var p;
      try {
        p = orig.apply(self, args);
      } catch (err) {
        /* a synchronous throw must not wedge the queue */
        free();
        throw err;
      }
      p.then(noop, noop).then(free);
      return p;
    });

    return run; /* caller gets the real Response promise, unchanged */
  };

  S.state = 'active';
})();
